import { type FC, useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Modal from "../../modal/Modal";
import type {
  AcrScanResult,
  AcrScanStatus,
  AcrAiDetectionItem,
} from "../../../types/acr";
import {
  useGetAcrFileStatusQuery,
  useRescanFileMutation,
} from "../../../app/services/acrFilescanning";
import {
  capAcrScore as capScore,
  formatAcrArtists as formatArtists,
  formatAcrTime as formatTime,
  formatAcrTimeRange as formatTimeRange,
} from "../../../utils/acr";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../../date-display/DateDisplay";
import Button from "../../button/Button";
import IconButton from "../../button/IconButton";
import ToolTip from "../../tooltip/ToolTip";

export interface MediaDetailModalProps {
  onClose: () => void;
  fileName?: string;
  width?: number;
  height?: number;
  fps?: number;
  fileSize?: number;
  acrScanResult?: AcrScanResult | null;
  fileId?: string;
  acrScanStatus?: AcrScanStatus;
  acrScannedAt?: string | null;
  onRescanSuccess?: () => void;
}

// ─── ヘルパー ──────────────────────────────────────────
// 楽曲照合の整形ヘルパー（capScore / formatArtists / formatTime / formatTimeRange）は
// 管理者・INFO の一覧表と共通のため utils/acr に集約している（上の import を参照）。

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const getAiDetectionColor = (item: AcrAiDetectionItem): string => {
  if (item.prediction === "human") return "var(--soremo-light-gray)";
  if (item.ai_probability >= 90) return "var(--soremo-green)";
  return "var(--soremo-blue)";
};

/**
 * 楽曲タイトル（＋アーティスト名）で Google 検索を新規タブで開く。
 * 旧 Django 実装（project_detail.js の .ACR-item-right クリック）の踏襲。
 */
const openAcrSearch = (title: string, artist?: string): void => {
  const query = [title, artist]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(" ");
  if (!query) return;
  window.open(
    `https://www.google.com/search?q=${encodeURIComponent(query)}`,
    "_blank",
    "noopener,noreferrer",
  );
};

// ─── ACR結果カード styled-components ───────────────────

const AcrSectionGroup = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AcrSectionCard = styled.div<{ $bg: string }>`
  border-radius: 4px;
  background-color: ${(props) => props.$bg};
  padding: 12px 8px;
`;

const AcrSectionCardTitle = styled.div.attrs({
  className: "heading-13 u-line-height-100",
})`
  color: #fff;
  margin-bottom: 8px;
`;

const AcrSectionCardInner = styled.div`
  background-color: #fff;
  border-radius: 4px;
  padding: 4px 8px;
`;

const AcrCard = styled.div`
  display: flex;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const AcrCoverColumn = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 56px;
`;

const AcrCoverArt = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 4px;
  overflow: hidden;
  background-color: var(--soremo-background);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .material-symbols-rounded {
    font-size: 28px;
    color: var(--soremo-light-gray);
  }
`;

const MiniScoreBar = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
`;

const MiniScoreTrack = styled.div`
  width: 100%;
  height: 3px;
  background-color: #f0f0f0;
  border-radius: 1.5px;
  overflow: hidden;
`;

const MiniScoreFill = styled.div<{ $width: number }>`
  height: 100%;
  width: ${(props) => props.$width}%;
  background-color: var(--soremo-blue);
  border-radius: 1.5px;
`;

const MiniScoreText = styled.span`
  font-size: 8px;
  line-height: 1;
  color: var(--soremo-blue);
`;

const AcrCardInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AcrTitle = styled.div.attrs({
  className: "heading-13 u-line-height-150",
})`
  color: #000;
  word-break: break-word;
`;

const AcrArtistName = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: #000;
`;

const AcrTimeInfo = styled.div.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
  display: flex;
  gap: 32px;
`;

// 楽曲カード右端の検索ボタン。カード高に対して縦中央へ寄せる。
const AcrCardSearchButton = styled(IconButton)`
  align-self: center;
`;

// ─── AI Detection styled-components ───────────────────

const AiSection = styled.div`
  padding-top: 8px;
`;

const AiVerdictRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AiVerdictBadge = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 4px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background-color: ${(props) => props.$color};
`;

const AiVerdictPct = styled.span.attrs({
  className: "heading-13 u-line-height-100",
})`
  color: #fff;
`;

const AiVerdictInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AiVerdictLabel = styled.div.attrs({
  className: "bodytext-13 u-line-height-150",
})`
  color: var(--soremo-black);
`;

const AiVerdictMeta = styled.div.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
`;

const AiSourcesLine = styled.div.attrs({
  className: "label8 u-line-height-150",
})`
  color: var(--soremo-light-gray);
`;

// ─── ACR ステータスバッジ styled-components ────────────

const AcrStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AcrStatusBadge = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 4px 16px;
  border: 1px solid ${(props) => props.$color};
  border-radius: 0;
  font-size: 11px;
  line-height: 1.2;
  color: ${(props) => props.$color};
  white-space: nowrap;
`;

const AcrScannedAtText = styled.span.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
`;

// ─── 再スキャン styled-components ──────────────────────

const RescanArea = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RescanStatusText = styled.span.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  color: var(--soremo-light-gray);
`;

// ─── コンポーネント ────────────────────────────────────

const RESCANABLE_STATUSES: AcrScanStatus[] = ["matched", "no_match", "error"];

/** 照合中がこの時間を超えたら「スタックの可能性あり」として再照合ボタンを出す */
const STALE_PROCESSING_MS = 20 * 60 * 1000;

/** 照合中ステータスの自動更新間隔（開始直後） */
const ACR_FAST_POLL_INTERVAL_MS = 15 * 1000;

/** fast ポーリングを続ける時間。超過後は slow 間隔に落として追従する */
const ACR_FAST_POLL_DURATION_MS = 5 * 60 * 1000;

/** 長時間照合中が続く場合の自動更新間隔 */
const ACR_SLOW_POLL_INTERVAL_MS = 60 * 1000;

/** ポーリングで終端状態に達したときにローカル保持する ACR 結果 */
interface SettledAcr {
  status: AcrScanStatus;
  result: AcrScanResult | null;
  scannedAt: string | null;
}

/**
 * 楽曲カード（原盤 / カバー / カスタム）右端の検索ボタン。
 * アーティスト名があればタイトル＋アーティスト、無ければタイトルのみで検索する。
 */
const AcrSearchButton: FC<{ title: string; artist?: string }> = ({
  title,
  artist,
}) => {
  const label = artist ? "タイトル・アーティスト名で検索" : "タイトルで検索";
  return (
    <ToolTip description={label}>
      <AcrCardSearchButton
        icon="search"
        size={20}
        ariaLabel={label}
        onClick={() => openAcrSearch(title, artist)}
      />
    </ToolTip>
  );
};

const MediaDetailModal: FC<MediaDetailModalProps> = ({
  onClose,
  fileName,
  width,
  height,
  fps,
  fileSize,
  acrScanResult,
  fileId,
  acrScanStatus,
  acrScannedAt,
  onRescanSuccess,
}) => {
  const [rescanFile, { isLoading: isRescanning }] = useRescanFileMutation();
  // 再照合を実行したクライアント時刻。スタック判定の基準に加えることで、
  // 押下直後から一定時間は「照合中」表示に確実に切り替わる（ボタンが残らない）。
  const [rescanRequestedAt, setRescanRequestedAt] = useState<number | null>(
    null,
  );
  const [mountedAt] = useState(() => Date.now());
  // ポーリングが終端状態（matched / no_match / error）に達したときの結果。
  // RTK Query キャッシュは購読解除後に破棄されるため、ローカルに保持する。
  const [settled, setSettled] = useState<SettledAcr | null>(null);

  // 照合中のファイル（または再照合直後）のみステータスをポーリングし、完了を自動反映する
  const propsInProgress =
    acrScanStatus === "pending" || acrScanStatus === "processing";
  const shouldPoll =
    !!fileId && settled == null && (propsInProgress || rescanRequestedAt != null);

  // ポーリング応答のたびに再レンダーされるため、fast → slow の切替は
  // レンダー時の経過時間評価で足りる（専用タイマー不要）
  const fastPollBaseMs = rescanRequestedAt ?? mountedAt;
  const pollingIntervalMs =
    Date.now() - fastPollBaseMs > ACR_FAST_POLL_DURATION_MS
      ? ACR_SLOW_POLL_INTERVAL_MS
      : ACR_FAST_POLL_INTERVAL_MS;

  const { data: liveData, fulfilledTimeStamp } = useGetAcrFileStatusQuery(
    fileId ?? "",
    {
      skip: !shouldPoll,
      pollingInterval: pollingIntervalMs,
    },
  );

  // 再照合前の古いキャッシュを誤って採用しないよう、取得完了時刻で鮮度を確認する
  const liveIsFresh =
    liveData != null &&
    (rescanRequestedAt == null ||
      (fulfilledTimeStamp != null && fulfilledTimeStamp > rescanRequestedAt));
  const live = liveIsFresh ? liveData.data : undefined;

  // ポーリングが終端状態に達したら結果を確定し、ポーリングを停止する
  useEffect(() => {
    if (!live) return;
    if (
      live.acr_scan_status === "pending" ||
      live.acr_scan_status === "processing"
    ) {
      return;
    }
    setSettled({
      status: live.acr_scan_status,
      result: live.acr_scan_result,
      scannedAt: live.acr_scanned_at,
    });
  }, [live]);

  // 採用順: 確定結果 > ポーリング結果 > 再照合直後のローカル状態 > props
  const effectiveStatus: AcrScanStatus | undefined =
    settled?.status ??
    live?.acr_scan_status ??
    (rescanRequestedAt != null ? "pending" : acrScanStatus);
  const effectiveResult = settled
    ? settled.result
    : live
      ? live.acr_scan_result
      : acrScanResult;
  const effectiveScannedAt = settled
    ? settled.scannedAt
    : live
      ? live.acr_scanned_at
      : acrScannedAt;

  const isInProgress =
    effectiveStatus === "pending" || effectiveStatus === "processing";

  // スタック判定の基準時刻: BE の送信時刻と再照合実行時刻の新しい方。
  // どちらも無い場合（送信前）はスタック扱いにしない（BE 側のセーフティ
  // ネットが error に確定させる）。
  const scannedAtMs = effectiveScannedAt
    ? new Date(effectiveScannedAt).getTime()
    : 0;
  const staleBaseMs = Math.max(scannedAtMs, rescanRequestedAt ?? 0);
  const isStaleProcessing =
    isInProgress &&
    staleBaseMs > 0 &&
    Date.now() - staleBaseMs > STALE_PROCESSING_MS;
  const canRescan =
    !!fileId &&
    !!effectiveStatus &&
    (RESCANABLE_STATUSES.includes(effectiveStatus) || isStaleProcessing);

  const handleRescan = useCallback(async () => {
    if (!fileId || isRescanning) return;
    try {
      await rescanFile(fileId).unwrap();
      setSettled(null);
      setRescanRequestedAt(Date.now());
      onRescanSuccess?.();
    } catch {
      toast.error("再照合の開始に失敗しました。時間をおいてお試しください。");
    }
  }, [fileId, isRescanning, rescanFile, onRescanSuccess]);

  const music = effectiveResult?.music;
  const coverSongs = effectiveResult?.cover_songs;
  const customFiles = effectiveResult?.custom_files;
  const aiDetection = effectiveResult?.ai_detection;
  const scannedAt = effectiveScannedAt ?? effectiveResult?._acr_scanned_at;

  const hasAcrResult =
    (music && music.length > 0) ||
    (coverSongs && coverSongs.length > 0) ||
    (customFiles && customFiles.length > 0);
  const hasAiDetection = aiDetection && aiDetection.length > 0;
  const hasAcrContent = hasAcrResult || hasAiDetection;

  const hasMetadata = (width != null && height != null) || fileSize != null;

  const metadataDescription = hasMetadata ? (
    <>
      {width != null && height != null && (
        <>
          {width} x {height}
          {fps != null && ` ${fps}fps`}
        </>
      )}
      {width != null && height != null && fileSize != null && "　"}
      {fileSize != null && formatFileSize(fileSize)}
    </>
  ) : undefined;

  return (
    <Modal
      onClose={onClose}
      titleSize="compact"
      title={fileName || "詳細情報"}
      description={metadataDescription}
      descriptionTone="subtle"
      size={hasAcrContent ? "default" : "auto"}
    >
      {(hasAcrResult ||
        effectiveStatus === "matched" ||
        effectiveStatus === "no_match" ||
        effectiveStatus === "error") && (
        <AcrStatusRow>
          {hasAcrResult || effectiveStatus === "matched" ? (
            <AcrStatusBadge $color="var(--soremo-blue)">
              楽曲照合結果あり
            </AcrStatusBadge>
          ) : (
            <AcrStatusBadge $color="var(--soremo-light-gray)">
              楽曲照合結果なし
            </AcrStatusBadge>
          )}
          {scannedAt && (
            <AcrScannedAtText>
              <DateDisplay
                date={scannedAt}
                type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
              />
            </AcrScannedAtText>
          )}
        </AcrStatusRow>
      )}

      {hasAcrContent && (
        <AcrSectionGroup>
          {music && music.length > 0 && (
            <AcrSectionCard $bg="var(--soremo-blue)">
              <AcrSectionCardTitle>原盤</AcrSectionCardTitle>
              <AcrSectionCardInner>
                {music.map((item, i) => {
                  const score = capScore(item.result.score);
                  return (
                    <AcrCard key={i}>
                      <AcrCoverColumn>
                        <AcrCoverArt>
                          {item.cover_url ? (
                            <img src={item.cover_url} alt="" />
                          ) : (
                            <span className="material-symbols-rounded">
                              album
                            </span>
                          )}
                        </AcrCoverArt>
                        <MiniScoreBar>
                          <MiniScoreTrack>
                            <MiniScoreFill $width={score} />
                          </MiniScoreTrack>
                          <MiniScoreText>{score}%</MiniScoreText>
                        </MiniScoreBar>
                      </AcrCoverColumn>
                      <AcrCardInfo>
                        <AcrTitle>{item.result.title}</AcrTitle>
                        <AcrArtistName>
                          {formatArtists(item.result.artists)}
                        </AcrArtistName>
                        <AcrTimeInfo>
                          <span>
                            {formatTimeRange(
                              item.result.db_begin_time_offset_ms,
                              item.played_duration,
                            )}
                          </span>
                          <span>{formatTime(item.played_duration)}</span>
                        </AcrTimeInfo>
                      </AcrCardInfo>
                      <AcrSearchButton
                        title={item.result.title}
                        artist={formatArtists(item.result.artists)}
                      />
                    </AcrCard>
                  );
                })}
              </AcrSectionCardInner>
            </AcrSectionCard>
          )}

          {coverSongs && coverSongs.length > 0 && (
            <AcrSectionCard $bg="var(--soremo-light-gray)">
              <AcrSectionCardTitle>複製（カバーソング）</AcrSectionCardTitle>
              <AcrSectionCardInner>
                {coverSongs.map((item, i) => (
                  <AcrCard key={i}>
                    <AcrCoverColumn>
                      <AcrCoverArt>
                        {item.cover_url ? (
                          <img src={item.cover_url} alt="" />
                        ) : (
                          <span className="material-symbols-rounded">
                            album
                          </span>
                        )}
                      </AcrCoverArt>
                    </AcrCoverColumn>
                    <AcrCardInfo>
                      <AcrTitle>{item.result.title}</AcrTitle>
                      <AcrArtistName>
                        {formatArtists(item.result.artists)}
                      </AcrArtistName>
                    </AcrCardInfo>
                    <AcrSearchButton
                      title={item.result.title}
                      artist={formatArtists(item.result.artists)}
                    />
                  </AcrCard>
                ))}
              </AcrSectionCardInner>
            </AcrSectionCard>
          )}

          {customFiles && customFiles.length > 0 && (
            <AcrSectionCard $bg="var(--soremo-border)">
              <AcrSectionCardTitle>カスタムコンテンツ</AcrSectionCardTitle>
              <AcrSectionCardInner>
                {customFiles.map((item, i) => {
                  const score = capScore(item.result.score);
                  return (
                    <AcrCard key={i}>
                      <AcrCoverColumn>
                        <AcrCoverArt>
                          <span className="material-symbols-rounded">
                            album
                          </span>
                        </AcrCoverArt>
                        <MiniScoreBar>
                          <MiniScoreTrack>
                            <MiniScoreFill $width={score} />
                          </MiniScoreTrack>
                          <MiniScoreText>{score}%</MiniScoreText>
                        </MiniScoreBar>
                      </AcrCoverColumn>
                      <AcrCardInfo>
                        <AcrTitle>{item.result.title}</AcrTitle>
                        <AcrTimeInfo>
                          <span>
                            {formatTimeRange(
                              item.result.db_begin_time_offset_ms,
                              item.played_duration,
                            )}
                          </span>
                          <span>{formatTime(item.played_duration)}</span>
                        </AcrTimeInfo>
                      </AcrCardInfo>
                      <AcrSearchButton title={item.result.title} />
                    </AcrCard>
                  );
                })}
              </AcrSectionCardInner>
            </AcrSectionCard>
          )}

          {hasAiDetection &&
            aiDetection.map((item, i) => {
              const color = getAiDetectionColor(item);
              const isHuman = item.prediction === "human";
              const sortedSources = [...item.source_probabilities].sort(
                (a, b) => b.probability - a.probability,
              );
              const sourcesText = sortedSources
                .map((s) => `${s.source}: ${s.probability.toFixed(1)}%`)
                .join("、");

              return (
                <AiSection key={`ai-${i}`}>
                  <AiVerdictRow>
                    <AiVerdictBadge $color={color}>
                      <AiVerdictPct>
                        {item.ai_probability.toFixed(1)}%
                      </AiVerdictPct>
                    </AiVerdictBadge>
                    <AiVerdictInfo>
                      <AiVerdictLabel>
                        AI判定: {isHuman ? "Human" : "AI Generated"}
                      </AiVerdictLabel>
                      <AiVerdictMeta>
                        {formatTime(item.start)} - {formatTime(item.end)}
                      </AiVerdictMeta>
                      {sortedSources.length > 0 && (
                        <AiSourcesLine>{sourcesText}</AiSourcesLine>
                      )}
                    </AiVerdictInfo>
                  </AiVerdictRow>
                </AiSection>
              );
            })}
        </AcrSectionGroup>
      )}

      {fileId && effectiveStatus && (
        <RescanArea>
          {canRescan && (
            <Button
              size="small"
              theme="secondary"
              variant="outlined"
              width="fit"
              leftIcon={isRescanning ? "hourglass_empty" : "refresh"}
              disabled={isRescanning}
              onClick={handleRescan}
            >
              {isRescanning ? "送信中…" : "楽曲再照合"}
            </Button>
          )}
          {isInProgress && (
            <RescanStatusText>
              {isStaleProcessing
                ? "照合に時間がかかっています"
                : "楽曲照合中…"}
            </RescanStatusText>
          )}
        </RescanArea>
      )}
    </Modal>
  );
};

export default MediaDetailModal;
